/**
 * Informative counter-value of an invoice in a foreign currency (#146).
 *
 * The invoice is certified in its certification currency; the counter-value
 * is its net to pay divided by a rate, in the payable unit of the foreign
 * currency. It is never sent to the MCF and never enters a certified amount.
 */
import { CURRENCY_MINOR_UNITS, currencyCode } from "./money.js";
import { invoiceNetExact, toPayableAmount } from "./payable-amount.js";
/**
 * Fixed parity of the franc CFA (UEMOA) to the euro. A statutory constant,
 * not an observed rate: it has no date and is never read from `ExchangeRate`.
 */
export const XOF_PER_EUR = 655.957;
/**
 * The currency `ExchangeRate.rate` and the euro parity are counted in. A
 * counter-value is offered only on an invoice certified in it.
 */
export const COUNTER_VALUE_BASE_CURRENCY = "XOF";
/** The euro, whose counter-value always uses the fixed parity. */
export const EUR_FIXED_PARITY = Object.freeze({ currency: "EUR", rate: XOF_PER_EUR });
/**
 * `Invoice.counterValueRateSource` codes of a rate that is not an
 * `ExchangeRate` row's own source.
 */
export const COUNTER_VALUE_SOURCES = Object.freeze({
    FIXED_PARITY: "PARITE_FIXE",
    MANUAL: "MANUEL",
});
/**
 * Why `currency` cannot be the counter-value of an invoice certified in
 * `certificationCurrency`, in French, or null when it can.
 */
export function counterValueCurrencyRefusal(currency, certificationCurrency) {
    if (currencyCode(certificationCurrency) !== COUNTER_VALUE_BASE_CURRENCY) {
        return `La contre-valeur indicative n'est proposée que sur une facture certifiée en ${COUNTER_VALUE_BASE_CURRENCY}`;
    }
    if (currency === COUNTER_VALUE_BASE_CURRENCY) {
        return `La contre-valeur indicative se donne dans une devise étrangère, pas en ${COUNTER_VALUE_BASE_CURRENCY}`;
    }
    if (CURRENCY_MINOR_UNITS[currency] === undefined) {
        return `Devise de contre-valeur non prise en charge : « ${currency} »`;
    }
    return null;
}
/** Largest rate `Invoice.counterValueRate` (Decimal(18,6)) holds, exclusive. */
export const COUNTER_VALUE_RATE_LIMIT = 1_000_000_000_000;
/** Largest amount `Invoice.counterValueAmount` (Decimal(15,2)) holds, exclusive. */
export const COUNTER_VALUE_AMOUNT_LIMIT = 10_000_000_000_000;
/**
 * Why `rate` cannot be a counter-value rate, in French, or null when it can:
 * finite, above zero, at most six decimals, below COUNTER_VALUE_RATE_LIMIT.
 */
export function counterValueRateRefusal(rate) {
    const micro = rate * 1_000_000;
    if (!Number.isFinite(rate) ||
        rate <= 0 ||
        rate >= COUNTER_VALUE_RATE_LIMIT ||
        Math.abs(micro - Math.round(micro)) > 1e-4) {
        return `Taux de contre-valeur invalide : ${String(rate).replace(".", ",")} — un taux supérieur à zéro, d'au plus six décimales et inférieur à mille milliards est attendu`;
    }
    return null;
}
/** Whether a counter-value amount fits COUNTER_VALUE_AMOUNT_LIMIT. */
export function counterValueAmountFits(amount) {
    return Number.isFinite(amount) && Math.abs(amount) < COUNTER_VALUE_AMOUNT_LIMIT;
}
/** Whether `currency` takes the fixed euro parity rather than a rate. */
export function usesFixedParity(currency) {
    return currency === EUR_FIXED_PARITY.currency;
}
/**
 * The counter-value of an invoice: its exact net to pay (TTC + PSVB)
 * divided by `rate` (certification currency per unit of `currency`), in the
 * payable unit of `currency`.
 */
export function counterValueAmount(totals, rate, currency) {
    if (counterValueRateRefusal(rate) !== null) {
        throw new RangeError(`Taux de contre-valeur invalide : ${rate}`);
    }
    return toPayableAmount(invoiceNetExact(totals) / rate, currency);
}
//# sourceMappingURL=counter-value.js.map